import type { ActiveCommitmentPolicy, Cadence, DraftCommitmentBody } from '../types/commitments';

/**
 * Pure helpers for the contribution plan a client sets up on their own
 * (self-service, the primary path). The server re-checks everything and is the
 * authority; these exist so the client hears about an obvious mistake before
 * submitting, in the product's own terms.
 */

/** A plan outside the client's declared profile is referred, not accepted. */
export const REVIEW_REQUIRED_CODE = 'commitment_review_required';

export interface PlanForm {
  productId: string;
  cadence: Cadence;
  amount: string;
  startDate: string;
  dayOfMonth: string;
  dayOfWeek: string;
}

/** Local calendar date as YYYY-MM-DD (not UTC: "today" is the client's today). */
export function isoDate(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

export function defaultPlanForm(
  policy: ActiveCommitmentPolicy | null,
  productId: string,
  today: Date = new Date(),
): PlanForm {
  const cadences = policy?.allowed_cadences ?? [];
  const cadence: Cadence = cadences.includes('monthly') ? 'monthly' : (cadences[0] ?? 'monthly');
  return {
    productId,
    cadence,
    amount: policy?.min_amount ?? '',
    startDate: isoDate(today),
    dayOfMonth: String(policy?.default_day_of_month ?? 1),
    dayOfWeek: String(policy?.default_day_of_week ?? 0),
  };
}

function inRange(value: string, min: number, max: number): boolean {
  const n = Number(value);
  return Number.isInteger(n) && n >= min && n <= max;
}

function amountProblem(policy: ActiveCommitmentPolicy, amount: string): string | null {
  const value = Number(amount);
  if (!amount.trim() || !Number.isFinite(value) || value <= 0) {
    return 'Enter an amount greater than zero.';
  }
  if (value < Number(policy.min_amount)) {
    return `The minimum contribution for this series is ${policy.min_amount}.`;
  }
  if (policy.max_amount !== null && value > Number(policy.max_amount)) {
    return `The maximum contribution for this series is ${policy.max_amount}.`;
  }
  return null;
}

function scheduleProblem(form: PlanForm, today: Date): string | null {
  if (!form.startDate || form.startDate < isoDate(today)) {
    return 'Choose a start date from today onwards.';
  }
  if (form.cadence === 'monthly' && !inRange(form.dayOfMonth, 1, 28)) {
    return 'Choose a day of the month between 1 and 28.';
  }
  if (form.cadence === 'weekly' && !inRange(form.dayOfWeek, 0, 6)) {
    return 'Choose a day of the week.';
  }
  return null;
}

/** The first thing wrong with the form, in plain English, or null. */
export function planProblem(
  policy: ActiveCommitmentPolicy,
  form: PlanForm,
  today: Date = new Date(),
): string | null {
  if (!policy.allowed_cadences.includes(form.cadence)) {
    return 'Choose one of the schedules this series offers.';
  }
  return amountProblem(policy, form.amount) ?? scheduleProblem(form, today);
}

export function draftBody(clientId: string, form: PlanForm): DraftCommitmentBody {
  return {
    user_id: clientId,
    product_id: form.productId,
    cadence: form.cadence,
    amount: form.amount.trim(),
    start_date: form.startDate,
    end_date: null,
    day_of_month: form.cadence === 'monthly' ? Number.parseInt(form.dayOfMonth, 10) : null,
    day_of_week: form.cadence === 'weekly' ? Number.parseInt(form.dayOfWeek, 10) : null,
    notes: null,
  };
}
